import { CSSProperties, useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useScreen from 'Hooks/useScreen'
import useBrand from 'Hooks/useBrand'
import useMaestroChat from 'Hooks/useMaestroChat'
import { styx, clsx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import ButtonSymphony from 'elements/ButtonSymphony'
import CardSymphony from 'elements/CardSymphony'
import TagSymphony from 'elements/TagSymphony'
import StepSymphony from 'elements/StepSymphony'
import Banner from 'elements/Banner'
import { getMarketingPlan_api, getMarketingPlansForBrand_api, updateMarketingPlanActionStatus_api, MarketingPlan, MarketingPlanAction } from 'api/marketingPlanApi'
import AssistantWorkspaceActions, { ActionButton } from './AssistantWorkspaceActions'
import { WorkspaceTheme } from './AssistantWorkspaceWrapper'
import useSignupFlow, { WorkspaceType } from 'Hooks/useSignupFlow'

interface AssistantWorkspaceMarketingPlanProps {
    workspaceContent: {
        type: 'marketingPlan';
        data: {
            demoMarketingPlan?: MarketingPlan;
            isDemo?: boolean;
            scenarioId?: string;
            planId: string | null;
            name?: string | null;
            status?: string | null;
            type?: string | null;
            isLoading?: boolean;
            actions?: any[]; // Raw API actions data
        };
        title?: string;
        isLoading?: boolean;
    };
    handleSendMessage: (message: string, hiddenData?: any) => void;
}

const AssistantWorkspaceMarketingPlan = ({
    workspaceContent,
    handleSendMessage,
}: AssistantWorkspaceMarketingPlanProps) => {
    const { mobileView } = useScreen()
    const { currentBrand, temporarySessionId } = useBrand()
    const { isAuthenticated, loginWithPopup } = useAuth0()
    const { chatId } = useMaestroChat()

    const { data, isLoading: workspaceLoading } = workspaceContent
    const { planId } = data

    const [plan, setPlan] = useState<MarketingPlan | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    
    // Use the new signup flow store
    const { openSignupModal } = useSignupFlow()

    // Transform API data to match interface (snake_case to camelCase)
    const transformActionData = (apiAction: any): MarketingPlanAction & { order?: number } => {
        return {
            id: apiAction.id,
            name: apiAction.name,
            title: apiAction.title,
            description: apiAction.description,
            startDate: apiAction.start_date,
            endDate: apiAction.end_date,
            focus: apiAction.focus,
            platform: apiAction.platform,
            priority: apiAction.priority,
            status: apiAction.status,
            actionType: apiAction.action_type,
            expectedOutcomes: apiAction.expected_outcomes,
            playbookType: apiAction.playbook_type,
            goalLink: apiAction.goal_link,
            budget: apiAction.budget_amount ? {
                amount: parseFloat(apiAction.budget_amount),
                type: apiAction.budget_type
            } : undefined,
            targetingSummary: apiAction.targeting_summary,
            creativeBrief: apiAction.creative_brief,
            conversionEventName: apiAction.conversion_event_name,
            campaignType: apiAction.campaign_type,
            toolType: apiAction.tool_type,
            toolConfig: apiAction.tool_config,
            contentType: apiAction.content_type,
            contentIdeas: apiAction.content_ideas,
            executionNotes: apiAction.execution_notes,
            strategyType: apiAction.strategy_type,
            strategyDescription: apiAction.strategy_description,
            order: apiAction.order || apiAction.Order // Support both lowercase and uppercase
        }
    }

    // Load full plan data when component mounts or planId changes
    useEffect(() => {
        const loadPlanData = async () => {
            console.log('🔍 Marketing Plan Debug - workspaceContent data:', data)
            console.log('🔍 Marketing Plan Debug - planId:', planId)
            console.log('🔍 Marketing Plan Debug - actions:', data?.actions)
            console.log('🔍 Marketing Plan Debug - isAuthenticated:', isAuthenticated)

            // Check if demo marketing plan data is provided (for general demo or UI test)
            if (data?.demoMarketingPlan) {
                console.log('📊 Using demo marketing plan data')
                setPlan(data.demoMarketingPlan)
                setIsLoading(false)
                return
            }

            // Check if actions data is passed directly (from API response)
            if (data?.actions && Array.isArray(data.actions)) {
                console.log('📊 Creating plan from actions data, count:', data.actions.length)
                // Create a minimal plan structure with the actions
                const actionsData = data.actions.map(transformActionData)
                console.log('📊 Transformed actions:', actionsData)
                const mockPlan: MarketingPlan = {
                    id: planId || 'mock-plan',
                    brand_id: currentBrand?.id || 0,
                    name: data.name || 'Marketing Plan',
                    status: 'active',
                    plan_type: data.type as any || 'launch',
                    objective: 'Drive streaming engagement and fan growth',
                    target_outcomes: ['Increase streams', 'Build fanbase', 'Drive engagement'],
                    audience: {
                        primaryPersona: {
                            label: 'R&B/Hip Hop Fans',
                            age: '18-34',
                            geographies: ['United States', 'United Kingdom', 'Canada'],
                            interests: ['R&B', 'Hip Hop', 'Music Discovery']
                        }
                    },
                    budget: {
                        total: actionsData.reduce((sum: number, action: MarketingPlanAction) => sum + getActionBudgetAmount(action), 0),
                        tier: 'mid'
                    },
                    actions: actionsData
                }
                console.log('📊 Created mock plan:', mockPlan)
                setPlan(mockPlan)
                setIsLoading(false)
                return
            }

            if (!planId || !currentBrand?.id) {
                console.log('❌ No planId or currentBrand.id available')
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)
                const planData = await getMarketingPlan_api(currentBrand.id, planId)
                if (planData) {
                    // Transform actions if they need snake_case to camelCase conversion
                    if (planData.actions) {
                        const transformedActions = planData.actions.map(transformActionData)
                        planData.actions = transformedActions
                    }
                    setPlan(planData)
                } else {
                    console.warn('Marketing plan not found or returned null')
                }
            } catch (error) {
                console.error('Failed to load marketing plan:', error)
            } finally {
                setIsLoading(false)
            }
        }

        loadPlanData()
    }, [planId, currentBrand?.id, data])

    // Helper to get action budget as number
    const getActionBudgetAmount = (action: MarketingPlanAction): number => {
        if (!action.budget) return 0
        if (typeof action.budget === 'number') return action.budget
        return action.budget.amount || 0
    }

    const handleEditPlan = (section: string) => {
        handleSendMessage(`Edit my marketing plan ${section}`, {
            planId,
            editSection: section
        })
    }

    const handleSavePlan = () => {
        handleSendMessage(`Save my marketing plan`, {
            planId,
            action: 'save'
        })
    }

    const handleActionStart = (action: MarketingPlanAction, actionIndex: number) => {
        // Generate action-specific prompt with full context
        let prompt = `Based on my marketing plan "${plan?.name}", help me with this action: ${action.title}. `

        if (action.description) {
            prompt += `Details: ${action.description}. `
        }

        if (action.platform) {
            prompt += `Platform: ${action.platform}. `
        }

        if (action.toolType) {
            prompt += `This should use the ${action.toolType} Symphony tool. `
        }

        handleSendMessage(prompt, {
            planId,
            action: 'start',
            actionId: action.id,
            actionIndex
        })
    }

    const handleMarkComplete = async (action: MarketingPlanAction, actionIndex: number) => {
        if (!currentBrand?.id || !planId) {
            console.error('Missing brand ID or plan ID')
            return
        }

        const newStatus = action.status === 'completed' ? 'pending' : 'completed'

        try {
            const updatedPlan = await updateMarketingPlanActionStatus_api({
                brandId: currentBrand.id,
                planId,
                actionId: action.id,
                status: newStatus
            })

            if (updatedPlan) {
                // Update the local plan state with the updated plan
                setPlan(updatedPlan)
            }
        } catch (error) {
            console.error('Failed to update action status:', error)
            // Fallback to chat message if API fails
            const statusText = newStatus === 'completed' ? 'complete' : 'pending'
            handleSendMessage(`Mark this marketing plan action as ${statusText}: ${action.title}`, {
                planId,
                action: 'updateActionStatus',
                actionId: action.id,
                newStatus,
                actionIndex
            })
        }
    }

    const handlePromptAboutAction = (action: MarketingPlanAction) => {
        handleSendMessage(`I have a question about this marketing plan action: "${action.title}". `, {
            planId,
            action: 'question',
            actionId: action.id
        })
    }

    // Authentication and content gating logic
    const shouldShowFullPlan = () => {
        return isAuthenticated
    }

    const handleSignUpToUnlock = async () => {
        try {
            await openSignupModal(WorkspaceType.MARKETING_PLAN, {
                additionalStorageData: planId ? { 'marketing-plan-id': planId } : undefined,
                chatId: chatId || undefined,
                temporarySessionId: temporarySessionId || undefined,
                loginFunction: async () => {
                    await loginWithPopup({
                        authorizationParams: {
                            screen_hint: 'signup'
                        }
                    })
                }
            })
        } catch (error) {
            console.error('Signup failed:', error)
            // Error is handled by the store, just log it here
        }
    }

    const getBudgetTierColor = (tier?: string) => {
        if (!tier) return Colors.grey
        switch (tier) {
            case 'low': return Colors.green
            case 'mid': return Colors.purple
            case 'high': return Colors.orangeAnnotation
            default: return Colors.grey
        }
    }

    const getBudgetTierLabel = (tier?: string) => {
        if (!tier) return 'Custom'
        switch (tier) {
            case 'low': return 'Budget Friendly'
            case 'mid': return 'Balanced Approach'
            case 'high': return 'Premium Investment'
            default: return 'Custom'
        }
    }

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high': return Colors.red
            case 'medium': return Colors.orangeAnnotation
            case 'low': return Colors.green
            default: return Colors.grey
        }
    }

    const getActionIcon = (type: string) => {
        switch (type) {
            case 'symphony_tool': return 'wand'
            case 'executable_ad': return 'marketing'
            case 'on_platform_ad': return 'tv'
            case 'content_strategy': return 'edit'
            case 'external_strategy': return 'externalLink'
            default: return 'check'
        }
    }

    // Define action buttons for WorkspaceActions
    const getActionButtons = (): ActionButton[] => {
        if (!plan) return []

        // For unauthenticated users, show signup CTA
        if (!isAuthenticated) {
            return [
                {
                    text: 'Sign Up to Unlock',
                    onClick: handleSignUpToUnlock,
                    variant: 'primary',
                    icon: 'crown'
                }
            ]
        }

        // Existing authenticated user buttons
        const buttons: ActionButton[] = [
            {
                text: 'Save Plan',
                onClick: handleSavePlan,
                variant: 'primary',
                icon: 'download'
            },
            {
                text: 'Edit Budget',
                onClick: () => handleEditPlan('budget'),
                variant: 'outlined',
                style: {
                    borderColor: WorkspaceTheme.textSecondary,
                    color: WorkspaceTheme.textPrimary,
                    fontWeight: 500
                }
            },
            {
                text: 'Edit Audience',
                onClick: () => handleEditPlan('audience'),
                variant: 'outlined',
                style: {
                    borderColor: WorkspaceTheme.textSecondary,
                    color: WorkspaceTheme.textPrimary,
                    fontWeight: 500
                }
            },
            {
                text: 'Edit Timeline',
                onClick: () => handleEditPlan('timeline'),
                variant: 'outlined',
                style: {
                    borderColor: WorkspaceTheme.textSecondary,
                    color: WorkspaceTheme.textPrimary,
                    fontWeight: 500
                }
            }
        ]

        return buttons
    }

    const renderPlanHeader = () => {
        if (!plan) return null

        return (
            <div className="planHeaderSection" style={sty.planHeaderSection}>
                <div className="planTitleRow" style={sty.planTitleRow}>
                    <div className="planTitle" style={sty.planTitle}>
                        {plan.name}
                    </div>
                    {plan.plan_type && (
                        <TagSymphony
                            className="planTypeTag"
                            title={plan.plan_type.toUpperCase()}
                            icon={plan.plan_type === 'launch' ? 'lightening' : 'reload'}
                            color="none"
                            style={{
                                backgroundColor: plan.plan_type === 'launch' ? WorkspaceTheme.accentSoft : Colors.blue + '20',
                                color: plan.plan_type === 'launch' ? WorkspaceTheme.accent : Colors.blue,
                                border: `1px solid ${plan.plan_type === 'launch' ? WorkspaceTheme.accent : Colors.blue}40`
                            }}
                        />
                    )}
                </div>

                <div className="planObjective" style={sty.planObjective}>
                    {plan.objective}
                </div>

                {plan.target_outcomes.length > 0 && (
                    <div className="targetOutcomes" style={sty.targetOutcomes}>
                        <div className="outcomesLabel" style={sty.outcomesLabel}>Target Outcomes:</div>
                        <div className="outcomesList" style={sty.outcomesList}>
                            {plan.target_outcomes.map((outcome: string, index: number) => (
                                <TagSymphony
                                    key={index}
                                    className="outcomeTag"
                                    title={outcome}
                                    color="none"
                                    style={sty.outcomeTag}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderOverviewCards = () => {
        if (!plan) return null

        const tierColor = getBudgetTierColor(plan.budget.tier)
        const tierLabel = getBudgetTierLabel(plan.budget.tier)

        return (
            <div
                className="planOverviewCards"
                style={styx(sty.planOverview, mobileView && sty.planOverview_mobile)}
            >
                {/* Budget Card */}
                <CardSymphony className="budgetOverviewCard" style={sty.overviewCard}>
                    <div className="budgetHeader" style={sty.cardHeader}>
                        <Icon icon="dollarSign" size={20} className="cardIcon" />
                        <div className="cardTitle" style={sty.cardTitle}>Budget</div>
                    </div>

                    <div className="budgetInfo" style={sty.budgetInfo}>
                        <div className="budgetAmount" style={sty.budgetAmount}>
                            ${plan.budget.total.toLocaleString()}
                        </div>
                        <div className="budgetTier" style={{ ...sty.budgetTier, color: tierColor }}>
                            {tierLabel}
                        </div>
                    </div>

                    {plan.budget.total > 0 && plan.budget.breakdown && (
                        <div className="budgetBreakdown" style={sty.budgetBreakdown}>
                            <div className="breakdownTitle" style={sty.breakdownTitle}>Budget Breakdown:</div>
                            {plan.budget.breakdown.paid_ads && (
                                <div className="breakdownItem" style={sty.breakdownItem}>
                                    <span className="breakdownLabel" style={sty.breakdownLabel}>Paid Ads</span>
                                    <span className="breakdownValue" style={sty.breakdownValue}>
                                        ${plan.budget.breakdown.paid_ads.toLocaleString()}
                                    </span>
                                </div>
                            )}
                            {plan.budget.breakdown.content_creation && (
                                <div className="breakdownItem" style={sty.breakdownItem}>
                                    <span className="breakdownLabel" style={sty.breakdownLabel}>Content Creation</span>
                                    <span className="breakdownValue" style={sty.breakdownValue}>
                                        ${plan.budget.breakdown.content_creation.toLocaleString()}
                                    </span>
                                </div>
                            )}
                            {plan.budget.breakdown.tools && (
                                <div className="breakdownItem" style={sty.breakdownItem}>
                                    <span className="breakdownLabel" style={sty.breakdownLabel}>Tools</span>
                                    <span className="breakdownValue" style={sty.breakdownValue}>
                                        ${plan.budget.breakdown.tools.toLocaleString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </CardSymphony>

                {/* Audience Card */}
                <CardSymphony className="audienceOverviewCard" style={sty.overviewCard}>
                    <div className="audienceHeader" style={sty.cardHeader}>
                        <Icon icon="audiences" size={20} className="cardIcon" />
                        <div className="cardTitle" style={sty.cardTitle}>Target Audience</div>
                    </div>

                    <div className="primaryPersona" style={sty.primaryPersona}>
                        <div className="personaLabel" style={sty.personaLabel}>
                            {plan.audience.primaryPersona.label}
                        </div>
                        <div className="personaDetails" style={sty.personaDetails}>
                            <span className="personaAge" style={sty.personaAge}>
                                {plan.audience.primaryPersona.age}
                            </span>
                            {plan.audience.primaryPersona.geographies.length > 0 && (
                                <span className="personaGeo" style={sty.personaGeo}>
                                    {plan.audience.primaryPersona.geographies.slice(0, 3).join(', ')}
                                    {plan.audience.primaryPersona.geographies.length > 3 && '...'}
                                </span>
                            )}
                        </div>
                    </div>

                    {plan.audience.primaryPersona.interests.length > 0 && (
                        <div className="personaInterests" style={sty.personaInterests}>
                            {plan.audience.primaryPersona.interests.slice(0, 3).map((interest, index) => (
                                <TagSymphony
                                    key={index}
                                    className="interestTag"
                                    title={interest}
                                    color="none"
                                    style={sty.interestTag}
                                />
                            ))}
                            {plan.audience.primaryPersona.interests.length > 3 && (
                                <span style={sty.interestOverflow}>
                                    +{plan.audience.primaryPersona.interests.length - 3} more
                                </span>
                            )}
                        </div>
                    )}
                </CardSymphony>
            </div>
        )
    }

    const renderTimeline = () => {
        if (!plan || !plan.actions || plan.actions.length === 0) return null

        const showFullContent = shouldShowFullPlan()
        const actionsToShow = showFullContent ? plan.actions : plan.actions.slice(0, 2)
        
        console.log('🎬 renderTimeline - plan.actions:', plan.actions)
        console.log('🎬 renderTimeline - showFullContent:', showFullContent)
        console.log('🎬 renderTimeline - actionsToShow:', actionsToShow)
        
        // Sort actions by order if available, otherwise by start date
        const sortedActions = [...actionsToShow].sort((a, b) => {
            const orderA = (a as any).order || 0
            const orderB = (b as any).order || 0
            
            // If both have order values, sort by order
            if (orderA && orderB) {
                return orderA - orderB
            }
            
            // If only one has order, prioritize it
            if (orderA && !orderB) return -1
            if (!orderA && orderB) return 1
            
            // If neither has order, sort by start date
            return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        })

        return (
            <div className="timelineSection" style={sty.timelineSection}>
                <div className="timelineHeader" style={sty.timelineHeader}>
                    <div className="timelineTitle" style={sty.timelineTitle}>Marketing Actions</div>
                    <div className="actionsCount" style={sty.actionsCount}>
                        {plan.actions.length} actions
                    </div>
                </div>

                <div className="actionsList" style={sty.actionsList}>
                    {sortedActions.map((action, index) => renderActionCard(action, index))}

                    {!showFullContent && plan.actions.length > 2 && (
                        <div className="gatedContent" style={sty.gatedContent}>
                                                    <div className="gatedActionPreview" style={sty.gatedActionPreview}>
                            <Icon icon="crown" size={20} className="lockIcon" style={sty.lockIcon} />
                            <div className="gatedText" style={sty.gatedText}>
                                    <div className="gatedTitle" style={sty.gatedTitle}>
                                        + {plan.actions.length - 2} more strategic actions
                                    </div>
                                    <div className="gatedDescription" style={sty.gatedDescription}>
                                        Get access to the complete action plan with detailed steps, budgets, and Symphony tool integrations
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const formatDateRange = (startDate: string, endDate: string) => {
        // Handle invalid date formats from API (like "0715-01-01")
        let start: Date, end: Date
        
        try {
            // If date looks malformed (year < 1000), fix it
            const fixedStartDate = startDate.replace(/^0(\d{3})/, '2$1') // 0715 -> 2715, then we'll fix to current year
            const fixedEndDate = endDate.replace(/^0(\d{3})/, '2$1')
            
            start = new Date(fixedStartDate)
            end = new Date(fixedEndDate)
            
            // If year is still way off, default to current year
            const currentYear = new Date().getFullYear()
            if (start.getFullYear() > currentYear + 100 || start.getFullYear() < 2000) {
                start = new Date(startDate.replace(/^\d{4}/, currentYear.toString()))
                end = new Date(endDate.replace(/^\d{4}/, currentYear.toString()))
            }
        } catch (error) {
            // Fallback to reasonable defaults
            start = new Date()
            end = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000) // 14 days from now
        }

        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }

        if (start.getFullYear() !== new Date().getFullYear()) {
            options.year = 'numeric'
        }

        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
    }

    const renderActionCard = (action: MarketingPlanAction, actionIndex: number) => {
        const priorityColor = getPriorityColor(action.priority)
        const actionIcon = getActionIcon(action.actionType)
        const isCompleted = action.status === 'completed'
        const isSymphonyTool = action.actionType === 'symphony_tool'
        const budgetAmount = getActionBudgetAmount(action)
        const showFullContent = shouldShowFullPlan()

        return (
            <div key={action.id} className="actionCard" style={styx(sty.actionCard, isCompleted && sty.actionCard_completed)}>
                <div className="actionHeader" style={sty.actionHeader}>
                    <div 
                        className="actionIconContainer" 
                        style={styx(
                            sty.actionIconContainer,
                            isCompleted && sty.actionIconContainer_completed
                        )}
                    >
                        {isCompleted ? (
                            <Icon icon="check" size={16} className="actionIcon" style={{ color: Colors.white }} />
                        ) : (
                            <Icon icon={actionIcon} size={16} className="actionIcon" style={{ color: WorkspaceTheme.accent }} />
                        )}
                    </div>
                    <div className="actionInfo" style={sty.actionInfo}>
                        <div className="actionTitle" style={sty.actionTitle}>
                            {action.title}
                            {isCompleted && showFullContent && (
                                <Icon
                                    icon="check"
                                    size={14}
                                    className="actionCompleteIcon"
                                    style={sty.actionCompleteIcon}
                                />
                            )}
                            {!showFullContent && (
                                <Icon
                                    icon="crown"
                                    size={14}
                                    className="actionLockedIcon"
                                    style={sty.actionLockedIcon}
                                />
                            )}
                        </div>
                        <div 
                            className="actionDescription" 
                            style={styx(
                                sty.actionDescription,
                                !showFullContent && sty.blurredContent
                            )}
                        >
                            {action.description}
                        </div>
                    </div>
                    <div className="actionMeta" style={sty.actionMeta}>
                        {showFullContent ? (
                            <TagSymphony
                                className="priorityTag"
                                title={action.priority.toUpperCase()}
                                color="none"
                                style={{
                                    backgroundColor: priorityColor + '20',
                                    color: priorityColor,
                                    border: `1px solid ${priorityColor}40`,
                                    fontSize: 11,
                                    fontWeight: 600
                                }}
                            />
                        ) : (
                            <div style={styx(sty.blurredTag, sty.blurredContent)}>
                                HIGH
                            </div>
                        )}
                    </div>
                </div>

                <div 
                    className="actionDetails" 
                    style={styx(
                        sty.actionDetails,
                        !showFullContent && sty.blurredContent
                    )}
                >
                    <div className="actionDates" style={sty.actionDetail}>
                        <Icon className="detailIcon" icon="calendar" size={12} />
                        <span>{showFullContent ? formatDateRange(action.startDate, action.endDate) : 'Jan 1 - Jan 15'}</span>
                    </div>
                    {action.platform && (
                        <div className="actionPlatform" style={sty.actionDetail}>
                            <Icon className="detailIcon" icon="globe" size={12} />
                            <span>{showFullContent ? action.platform : 'Platform'}</span>
                        </div>
                    )}
                    {(budgetAmount > 0 || !showFullContent) && (
                        <div className="actionBudget" style={sty.actionDetail}>
                            <Icon className="detailIcon" icon="dollarSign" size={12} />
                            <span>{showFullContent ? `$${budgetAmount}` : '$2,000'}</span>
                        </div>
                    )}
                </div>

                {(isSymphonyTool || !showFullContent) && (
                    <div 
                        className="symphonyToolBadge" 
                        style={styx(
                            sty.symphonyToolBadge,
                            !showFullContent && sty.blurredContent
                        )}
                    >
                        <Icon className="symphonyIcon" icon="wand" size={12} />
                        <span>Symphony Tool</span>
                    </div>
                )}

                {showFullContent ? (
                    <div className="actionButtons" style={sty.actionButtons}>
                        <ButtonSymphony
                            className="promptButton"
                            variant="outlined"
                            size="small"
                            onClick={() => handlePromptAboutAction(action)}
                            style={sty.actionButton}
                        >
                            <Icon icon="message" size={14} className="promptIcon" />
                            Ask
                        </ButtonSymphony>

                        {!isCompleted && (
                            <ButtonSymphony
                                className="startButton"
                                variant={isSymphonyTool ? "primary" : "outlined"}
                                size="small"
                                onClick={() => handleActionStart(action, actionIndex)}
                                style={sty.actionButton}
                            >
                                <Icon icon="play" size={14} className="startIcon" />
                                Start
                            </ButtonSymphony>
                        )}

                        <ButtonSymphony
                            className="completeButton"
                            variant={isCompleted ? "primary" : "outlined"}
                            size="small"
                            onClick={() => handleMarkComplete(action, actionIndex)}
                            style={sty.actionButton}
                        >
                            <Icon icon="check" size={14} className="completeIcon" />
                            {isCompleted ? "Completed" : "Mark Done"}
                        </ButtonSymphony>
                    </div>
                ) : (
                    <div className="lockedActionOverlay" style={sty.lockedActionOverlay}>
                        <Icon icon="crown" size={16} className="lockIcon" />
                        <span style={sty.unlockText}>Sign up to access action details</span>
                    </div>
                )}
            </div>
        )
    }

    if (isLoading || !plan) {
        const showNotFound = !isLoading && planId && currentBrand?.id

        return (
            <div className="assistantWorkspaceMarketingPlanLoading" style={sty.loadingContainer}>
                <Icon
                    icon="loading"
                    size={48}
                    className="loadingIcon"
                    style={sty.loadingIcon}
                />
                <div style={sty.loadingText}>
                    {isLoading ? 'Loading marketing plan...' :
                        showNotFound ? 'Marketing plan not found' :
                            'Marketing plan data unavailable'}
                </div>
            </div>
        )
    }

    console.log('🎨 Final render - plan:', plan)
    console.log('🎨 Final render - plan.actions length:', plan?.actions?.length)

    return (
        <div
            className={clsx("assistantWorkspaceMarketingPlan", mobileView && "mobile")}
            style={styx(sty.container, mobileView && sty.container_mobile)}
        >
            <div className="containerContent" style={sty.containerContent}>
                {renderPlanHeader()}
                {renderOverviewCards()}
                {renderTimeline()}
            </div>

            {/* Use AssistantWorkspaceActions instead of custom actions */}
            <AssistantWorkspaceActions
                icon="wand"
                title={!isAuthenticated ? "Unlock your Full Plan" : "Marketing Plan Actions"}
                description={!isAuthenticated ? "Create an account and subscribe to Starter to unlock Maestro's Marketing Plan skill." : "Save your plan or make edits to refine your strategy"}
                buttons={getActionButtons()}
                sticky={true}
            />
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 0,
        width: '100%',
        minHeight: 500,
        backgroundColor: WorkspaceTheme.primary,
    },
    container_mobile: {
        gap: 20,
    },

    containerContent: {
        padding: 24,
    },

    // Header Section
    planHeaderSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        paddingBottom: 20,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
    },
    planTitleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    planTitle: {
        ...Sty.text24bold,
        color: WorkspaceTheme.textPrimary,
        flex: 1,
    },
    planObjective: {
        fontSize: 16,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },
    targetOutcomes: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    outcomesLabel: {
        ...Sty.text14bold,
        color: WorkspaceTheme.textPrimary,
    },
    outcomesList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
    },
    outcomeTag: {
        backgroundColor: WorkspaceTheme.accentSoft,
        color: WorkspaceTheme.accent,
        border: `1px solid ${WorkspaceTheme.border}`,
    },

    // Plan Overview Section
    planOverview: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 20,
        marginBottom: 32,
    },
    planOverview_mobile: {
        gridTemplateColumns: '1fr',
    },

    // Overview Cards
    overviewCard: {
        padding: 20,
        border: `1px solid ${WorkspaceTheme.border}`,
        borderRadius: 12,
        backgroundColor: WorkspaceTheme.surface,
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
    },
    cardTitle: {
        ...Sty.text16bold,
        color: WorkspaceTheme.textPrimary,
    },

    // Budget Card
    budgetInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 16,
    },
    budgetAmount: {
        ...Sty.text20bold,
        color: WorkspaceTheme.textPrimary,
    },
    budgetTier: {
        fontSize: 14,
        fontWeight: 600,
    },
    budgetBreakdown: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    breakdownTitle: {
        ...Sty.text14bold,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 4,
    },
    breakdownItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: WorkspaceTheme.surfaceLight,
        borderRadius: 6,
    },
    breakdownLabel: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
    },
    breakdownValue: {
        fontSize: 14,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },

    // Audience Card
    primaryPersona: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    personaLabel: {
        ...Sty.text14bold,
        color: WorkspaceTheme.textPrimary,
    },
    personaDetails: {
        display: 'flex',
        gap: 16,
        fontSize: 13,
        color: WorkspaceTheme.textSecondary,
    },
    personaAge: {},
    personaGeo: {},
    personaInterests: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 8,
    },
    interestTag: {
        backgroundColor: Colors.blue + '15',
        color: Colors.blue,
        border: `1px solid ${Colors.blue}30`,
        fontSize: 11,
    },
    interestOverflow: {
        fontSize: 12,
        color: WorkspaceTheme.textTertiary,
        fontStyle: 'italic',
    },

    // Timeline Section
    timelineSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        marginTop: 24,
    },
    timelineHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 8,
    },
    timelineTitle: {
        ...Sty.text18bold,
        color: WorkspaceTheme.textPrimary,
    },
    actionsCount: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        backgroundColor: WorkspaceTheme.surfaceLight,
        padding: '4px 12px',
        borderRadius: 12,
    },

    // Actions
    actionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    actionCard: {
        border: `1px solid ${WorkspaceTheme.border}`,
        borderRadius: 8,
        padding: 20,
        backgroundColor: WorkspaceTheme.surface,
        transition: 'all 0.2s ease',
    },
    actionCard_completed: {
        backgroundColor: Colors.green + '05',
        borderColor: Colors.green + '40',
    },
    actionHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 16,
    },
    actionIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 6,
        backgroundColor: WorkspaceTheme.accentSoft,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    actionIconContainer_completed: {
        backgroundColor: Colors.green,
    },
    actionInfo: {
        flex: 1,
        minWidth: 0,
    },
    actionTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        lineHeight: 1.3,
    },
    actionCompleteIcon: {
        color: Colors.green,
    },
    actionDescription: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.5,
    },
    actionMeta: {},
    actionDetails: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 16,
        marginTop: 12,
    },
    actionDetail: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: WorkspaceTheme.textSecondary,
        fontWeight: 500,
    },
    symphonyToolBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        backgroundColor: WorkspaceTheme.accentSoft,
        color: WorkspaceTheme.accent,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 16,
        alignSelf: 'flex-start',
    },
    actionButtons: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
    },
    actionButton: {
        fontSize: 13,
        gap: 6,
        padding: '8px 16px',
        fontWeight: 500,
        border: `1px solid ${WorkspaceTheme.textSecondary}`,
        color: WorkspaceTheme.textSecondary,
        backgroundColor: 'transparent',
    },

    // Loading State
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 60,
        gap: 20,
        backgroundColor: WorkspaceTheme.primary,
    },
    loadingIcon: {
        color: WorkspaceTheme.accent,
    },
    loadingText: {
        fontSize: 16,
        color: WorkspaceTheme.textSecondary,
        textAlign: 'center',
    },

    // Gated Content Styles
    gatedContent: {
        marginTop: 24,
    },
    gatedActionPreview: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: 24,
        border: `2px dashed ${WorkspaceTheme.border}`,
        borderRadius: 12,
        backgroundColor: WorkspaceTheme.surfaceLight,
        textAlign: 'left',
    },
    lockIcon: {
        color: WorkspaceTheme.textTertiary,
        flexShrink: 0,
    },
    gatedText: {
        flex: 1,
    },
    gatedTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 4,
    },
    gatedDescription: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },

    // Blur Effect Styles
    blurredContent: {
        filter: 'blur(4px)',
        userSelect: 'none',
        pointerEvents: 'none',
    },
    actionLockedIcon: {
        color: WorkspaceTheme.textTertiary,
    },
    blurredTag: {
        padding: '4px 8px',
        backgroundColor: WorkspaceTheme.surfaceLight,
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        color: WorkspaceTheme.textTertiary,
        border: `1px solid ${WorkspaceTheme.border}`,
    },
    lockedActionOverlay: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 16,
        backgroundColor: WorkspaceTheme.surfaceLight,
        borderRadius: 6,
        border: `1px solid ${WorkspaceTheme.border}`,
        marginTop: 12,
    },
    unlockText: {
        fontSize: 13,
        color: WorkspaceTheme.textSecondary,
        fontWeight: 500,
    },
}

export default AssistantWorkspaceMarketingPlan 